import { Link } from 'react-router-dom';
import { LegalPageShell, legalStyles as s } from '../components/LegalPageShell';

export function TermsPage() {
  return (
    <LegalPageShell title="Terms of Use">
      <p className="text-slate-600 font-medium leading-relaxed -mt-4 mb-2">
        This page includes our Terms &amp; Conditions, Shipping Policy, and Cancellation &amp; Refund Policy.
      </p>

      {/* Section 1: Terms & Conditions */}
      <section className="pt-4 border-t border-slate-200">
        <h2 className="text-2xl font-black text-slate-900 mb-6">Terms &amp; Conditions</h2>
        <p className="mb-6">
          Welcome to BidMaster. By accessing or using our platform, website, or services, you agree to comply with and be
          bound by the following Terms and Conditions. If you do not agree with any part of these terms, please do not use
          our services.
        </p>

        <h3 className={s.h3}>1. Definitions</h3>
        <ul className={`${s.ul} mb-6`}>
          <li>
            <strong className="text-slate-800">“BidMaster”</strong> refers to the reverse auction platform operated by
            PRAV Solutions.
          </li>
          <li>
            <strong className="text-slate-800">“User”</strong> refers to any individual, organization, bidder, buyer,
            participant, or visitor using the platform.
          </li>
          <li>
            <strong className="text-slate-800">“Services”</strong> refers to all software, subscription services, bidding
            modules, procurement tools, and related functionalities provided through BidMaster.
          </li>
        </ul>

        <h3 className={s.h3}>2. Use of the Platform</h3>
        <p className="mb-3">Users agree to:</p>
        <ul className={`${s.ul} mb-6`}>
          <li>Provide accurate and complete registration information.</li>
          <li>Use the platform only for lawful procurement and bidding activities.</li>
          <li>Maintain confidentiality of account credentials.</li>
          <li>Not misuse, manipulate, disrupt, or attempt unauthorized access to the platform.</li>
        </ul>
        <p className="mb-6">
          BidMaster reserves the right to suspend or terminate accounts found violating these terms.
        </p>

        <h3 className={s.h3}>3. Subscription &amp; Payments</h3>
        <ul className={`${s.ul} mb-6`}>
          <li>Certain services on BidMaster require paid subscriptions.</li>
          <li>Subscription fees are billed through authorized payment gateways including Razorpay.</li>
          <li>Users are responsible for timely renewal of subscriptions.</li>
          <li>Failure to renew may result in restricted access to services.</li>
          <li>Pricing may change at any time with prior notice.</li>
        </ul>

        <h3 className={s.h3}>4. Reverse Auction Participation</h3>
        <p className="mb-3">Users participating in auctions agree:</p>
        <ul className={`${s.ul} mb-6`}>
          <li>All bids submitted are intentional and legally binding.</li>
          <li>
            BidMaster only provides the technology platform and is not responsible for commercial disputes between
            participants.
          </li>
          <li>Auction creators are solely responsible for procurement decisions.</li>
        </ul>

        <h3 className={s.h3}>5. Intellectual Property</h3>
        <p className="mb-6">
          All platform content including logos, software, graphics, UI designs, and branding are the intellectual
          property of BidMaster and may not be copied, reproduced, or distributed without written permission.
        </p>

        <h3 className={s.h3}>6. Limitation of Liability</h3>
        <p className="mb-3">BidMaster shall not be liable for:</p>
        <ul className={`${s.ul} mb-6`}>
          <li>Business losses arising from bidding decisions.</li>
          <li>Technical interruptions beyond reasonable control.</li>
          <li>Unauthorized access caused due to user negligence.</li>
          <li>Indirect or consequential damages.</li>
        </ul>

        <h3 className={s.h3}>7. Modifications</h3>
        <p className="mb-6">
          BidMaster reserves the right to modify these Terms &amp; Conditions at any time. Continued use of the platform
          constitutes acceptance of updated terms.
        </p>

        <h3 className={s.h3}>8. Governing Law</h3>
        <p>These Terms shall be governed by and interpreted under the laws of India.</p>
      </section>

      {/* Section 3: Shipping Policy */}
      <section className="pt-10 border-t border-slate-200">
        <h2 className="text-2xl font-black text-slate-900 mb-6">Shipping Policy</h2>
        <p className="mb-4">BidMaster is a digital software and subscription-based platform. No physical products are shipped.</p>
        <p className="mb-3">Upon successful payment:</p>
        <ul className={`${s.ul} mb-6`}>
          <li>Subscription access is activated digitally.</li>
          <li>Users receive access credentials and subscription confirmation electronically.</li>
          <li>Service activation generally occurs instantly or within a reasonable processing time.</li>
        </ul>
        <p>
          For any activation-related issues, please{' '}
          <Link to="/contact" className={s.link}>
            contact our support team
          </Link>
          .
        </p>
      </section>

      {/* Section 5: Cancellation & Refund Policy */}
      <section className="pt-10 border-t border-slate-200">
        <h2 className="text-2xl font-black text-slate-900 mb-6">Cancellation &amp; Refund Policy</h2>

        <h3 className={s.h3}>1. Subscription Cancellation</h3>
        <p className="mb-6">
          Users may cancel their subscription renewal at any time. Cancellation will prevent future recurring billing but
          will not affect the active subscription period already paid for.
        </p>

        <h3 className={s.h3}>2. Refund Eligibility</h3>
        <p className="mb-3">Refunds may only be considered under the following conditions:</p>
        <ul className={`${s.ul} mb-3`}>
          <li>Duplicate payment transactions</li>
          <li>Technical issues preventing access to services</li>
          <li>Incorrect billing due to system error</li>
        </ul>
        <p className="mb-6">Refund requests must be submitted within 7 days of the transaction.</p>

        <h3 className={s.h3}>3. Non-Refundable Situations</h3>
        <p className="mb-3">Refunds will generally not be provided for:</p>
        <ul className={`${s.ul} mb-6`}>
          <li>Partial usage of subscription period</li>
          <li>Failure to use the platform</li>
          <li>User dissatisfaction unrelated to technical malfunction</li>
          <li>Expired subscription periods</li>
        </ul>

        <h3 className={s.h3}>4. Refund Processing</h3>
        <p className="mb-6">
          Approved refunds will be processed through the original payment method within 7–10 business days.
        </p>

        <h3 className={s.h3}>5. Contact for Refunds</h3>
        <p className="mb-3">
          For cancellation or refund requests, contact{' '}
          <a href="mailto:support@bidmaster.co.in" className={s.link}>
            support@bidmaster.co.in
          </a>{' '}
          or visit our{' '}
          <Link to="/contact" className={s.link}>
            Contact Us
          </Link>{' '}
          page. Please include:
        </p>
        <ul className={s.ul}>
          <li>Registered email ID</li>
          <li>Transaction ID</li>
          <li>Payment date</li>
          <li>Reason for request</li>
        </ul>
      </section>
    </LegalPageShell>
  );
}
